<script setup lang="ts">
import { PoolNote } from '@/types/untangled/pool';
import PoolCard from './PoolCard.vue';

/**
 * TYPES
 */
type Props = {
  isLoading: boolean;
  poolNotes: PoolNote[];
  poolValueByPoolId: Record<string, number>;
};

/**
 * PROPS & EMITS
 */
const props = defineProps<Props>();

/**
 * STATES
 */
const noOfVisiblePoolNotes = ref(3);

/**
 * COMPUTED
 */
const visiblePoolNotes = computed(() => {
  return props.poolNotes.slice(0, noOfVisiblePoolNotes.value);
});

/**
 * HANDLERS
 */
const handleClickLoadMore = () => {
  noOfVisiblePoolNotes.value = noOfVisiblePoolNotes.value += 3;
};
</script>

<template>
  <div class="flex flex-col gap-5 w-full">
    <div
      :class="[
        'w-full',
        'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5',
      ]"
    >
      <template v-if="props.isLoading">
        <BalLoadingBlock class="w-full h-[444px] rounded-[12px]" />
        <BalLoadingBlock class="w-full h-[444px] rounded-[12px]" />
        <BalLoadingBlock class="w-full h-[444px] rounded-[12px]" />
      </template>

      <PoolCard
        v-for="poolNote in visiblePoolNotes"
        v-else
        :key="poolNote.id"
        :poolNote="poolNote"
        :poolValue="0"
      />
    </div>

    <div
      v-if="noOfVisiblePoolNotes < props.poolNotes.length"
      class="flex justify-center items-center"
    >
      <BalBtn
        v-if="false"
        color="gray"
        size="md"
        :disabled="props.isLoading"
        class="w-full dark:bg-gray-800 bg-white-50"
        :flat="true"
        @click="handleClickLoadMore"
      >
        Load more
        <BalIcon name="chevron-down" size="sm" class="ml-2" />
      </BalBtn>

      <div
        v-else
        class="w-full loadMoreBtn text-secondary"
        @click="handleClickLoadMore"
      >
        {{ $t('loadMore') }}
        <BalIcon name="chevron-down" size="sm" class="ml-2" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.loadMoreBtn {
  @apply flex items-center justify-center h-12 md:h-16 transition-all;
  @apply font-medium hover:text-purple-600 dark:hover:text-yellow-500;
  @apply dark:border-gray-900 rounded-lg;
  @apply shadow-lg;
  @apply bg-white hover:bg-gray-50 dark:bg-gray-850 dark:hover:bg-gray-800 cursor-pointer;
}
</style>
